import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Printer, Download, X, Building2, Phone, Mail, MapPin, Calendar, CreditCard, CheckCircle2, AlertCircle, Clock } from 'lucide-react';
import { clsx } from 'clsx';
import { useSettings } from '../context/SettingsContext';
import PharmacyLogo from './PharmacyLogo';

function StandardLayout({ purchase, settings, pharmacyName, pharmacyAddress, pharmacyPhone, pharmacyEmail, purchaseDate, items, subtotal, discount, tax, grandTotal, paidAmount, balanceDue, paymentStatus }) {
  return (
    <div className="w-full max-w-3xl bg-white p-8 border border-gray-200 rounded-xl shadow-sm print:border-none print:shadow-none print:p-0 text-gray-800">
      {/* Header: Pharmacy & Invoice info */}
      <div className="flex justify-between items-start border-b-2 border-emerald-600 pb-6 mb-6">
        <div className="flex items-start gap-3">
          <PharmacyLogo logo={settings?.pharmacy_logo} alt={`${pharmacyName} logo`} className="h-12 w-12 shrink-0 rounded-lg" imageClassName="h-12 w-12 shrink-0 rounded-lg object-contain" />
          <div>
          <h1 className="text-2xl font-black text-emerald-800 tracking-tight">{pharmacyName}</h1>
          <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
            <MapPin className="h-3.5 w-3.5 text-gray-400" /> {pharmacyAddress}
          </p>
          <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
            {pharmacyPhone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5 text-gray-400" /> {pharmacyPhone}</span>}
            {pharmacyEmail && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5 text-gray-400" /> {pharmacyEmail}</span>}
          </div>
          </div>
        </div>

        <div className="text-right">
          <span className="inline-block bg-emerald-50 text-emerald-800 text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-md border border-emerald-200">
            Purchase Order
          </span>
          <p className="font-mono text-base font-bold text-gray-900 mt-2">{purchase.reference_number}</p>
          <p className="text-xs text-gray-500 mt-0.5">Date: {purchaseDate}</p>
          <p className="text-xs text-gray-500">Method: <span className="font-semibold uppercase text-gray-800">{purchase.payment_method || 'cash'}</span></p>
        </div>
      </div>

      {/* Supplier & Status Details Banner */}
      <div className="grid grid-cols-2 gap-4 mb-6 bg-gray-50/80 p-4 rounded-xl border border-gray-100 text-xs">
        <div>
          <p className="font-bold text-[11px] text-gray-500 uppercase tracking-wider mb-1">Supplier Details</p>
          <p className="text-sm font-bold text-gray-900">{purchase.supplier_name || 'General Supplier'}</p>
          {purchase.supplier_phone && <p className="text-gray-600">Phone: {purchase.supplier_phone}</p>}
          {purchase.supplier_email && <p className="text-gray-600">Email: {purchase.supplier_email}</p>}
          {purchase.supplier_address && <p className="text-gray-600">Address: {purchase.supplier_address}</p>}
        </div>

        <div className="text-right flex flex-col justify-between items-end">
          <div>
            <p className="font-bold text-[11px] text-gray-500 uppercase tracking-wider mb-1">Payment Status</p>
            <span
              className={clsx(
                'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider',
                paymentStatus === 'paid'
                  ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                  : paymentStatus === 'partial'
                  ? 'bg-amber-100 text-amber-800 border border-amber-200'
                  : 'bg-red-100 text-red-800 border border-red-200'
              )}
            >
              {paymentStatus === 'paid' && <CheckCircle2 className="h-3.5 w-3.5" />}
              {paymentStatus === 'partial' && <Clock className="h-3.5 w-3.5" />}
              {paymentStatus === 'unpaid' && <AlertCircle className="h-3.5 w-3.5" />}
              {paymentStatus === 'unpaid' ? 'Pending (Unpaid)' : paymentStatus}
            </span>
          </div>
          {purchase.user_name && (
            <p className="text-[11px] text-gray-500 mt-2">Recorded by: <span className="font-semibold text-gray-700">{purchase.user_name}</span></p>
          )}
        </div>
      </div>

      {/* Items Table */}
      <div className="mb-6 overflow-hidden rounded-lg border border-gray-200">
        <table className="w-full text-left text-xs border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 font-bold uppercase text-[10px]">
              <th className="py-2.5 px-3">#</th>
              <th className="py-2.5 px-3">Medicine</th>
              <th className="py-2.5 px-3">Batch & Expiry</th>
              <th className="py-2.5 px-3 text-center">Unit</th>
              <th className="py-2.5 px-3 text-center">Qty</th>
              <th className="py-2.5 px-3 text-right">Buy Price</th>
              <th className="py-2.5 px-3 text-right">Discount</th>
              <th className="py-2.5 px-3 text-right">Total (Rs.)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-gray-700">
            {items.map((item, idx) => {
              const uQty = item.unit_quantity || item.quantity;
              const uName = item.unit_name || item.base_unit || 'units';
              const factor = parseInt(item.conversion_factor) || 1;
              const totalBaseQty = factor > 1 ? uQty * factor : null;
              const price = parseFloat(item.buy_price) || 0;
              const itemDisc = parseFloat(item.discount) || 0;
              const rowTotal = parseFloat(item.total) || (uQty * price * (1 - itemDisc / 100));

              return (
                <tr key={idx} className="hover:bg-gray-50/50">
                  <td className="py-2.5 px-3 text-gray-400 font-mono">{idx + 1}</td>
                  <td className="py-2.5 px-3">
                    <span className="font-bold text-gray-900">{item.medicine_name || `Medicine #${item.medicine_id}`}</span>
                    {totalBaseQty && (
                      <span className="block text-[10px] text-emerald-700 font-medium">
                        Restocks {totalBaseQty} {item.base_unit || 'base units'} (1 {uName} = {factor})
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-[11px]">
                    {item.batch_number && <span className="font-mono font-semibold text-gray-800">{item.batch_number}</span>}
                    {item.expiry_date && (
                      <span className="block text-[10px] text-gray-500">
                        Exp: {new Date(item.expiry_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-center font-semibold text-gray-800">{uName}</td>
                  <td className="py-2.5 px-3 text-center font-bold text-gray-900">{uQty}</td>
                  <td className="py-2.5 px-3 text-right font-medium">Rs. {price.toFixed(2)}</td>
                  <td className="py-2.5 px-3 text-right text-gray-500">{itemDisc > 0 ? `${itemDisc}%` : '-'}</td>
                  <td className="py-2.5 px-3 text-right font-bold text-gray-900">Rs. {rowTotal.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Totals & Notes Section */}
      <div className="grid grid-cols-2 gap-6 items-start mb-8">
        <div>
          {purchase.notes && (
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-xs">
              <p className="font-bold text-gray-600 uppercase text-[10px] tracking-wider mb-1">Notes / Terms:</p>
              <p className="text-gray-700 whitespace-pre-wrap">{purchase.notes}</p>
            </div>
          )}
          {purchase.supplier_balance !== undefined && parseFloat(purchase.supplier_balance) > 0 && (
            <div className="mt-3 p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-900 flex justify-between">
              <span className="font-medium">Total Supplier Outstanding Balance:</span>
              <span className="font-bold">Rs. {parseFloat(purchase.supplier_balance).toFixed(2)}</span>
            </div>
          )}
        </div>

        <div className="space-y-2 text-xs bg-gray-50/80 p-4 rounded-xl border border-gray-200">
          <div className="flex justify-between text-gray-600">
            <span>Subtotal:</span>
            <span className="font-semibold text-gray-900">Rs. {subtotal.toFixed(2)}</span>
          </div>
          {discount > 0 && (
            <div className="flex justify-between text-gray-600">
              <span>Total Item Discount:</span>
              <span className="font-semibold text-amber-600">- Rs. {discount.toFixed(2)}</span>
            </div>
          )}
          {tax > 0 && (
            <div className="flex justify-between text-gray-600">
              <span>Tax / Freight:</span>
              <span className="font-semibold text-gray-900">+ Rs. {tax.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm font-black border-t-2 border-gray-300 pt-2 text-gray-900">
            <span>Grand Total:</span>
            <span className="text-emerald-700">Rs. {grandTotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-gray-700 font-semibold pt-1 border-t border-gray-200">
            <span>Amount Paid:</span>
            <span className="text-gray-900">Rs. {paidAmount.toFixed(2)}</span>
          </div>
          <div className="flex justify-between font-bold pt-1">
            <span className={balanceDue > 0 ? 'text-red-600' : 'text-gray-600'}>Balance Due:</span>
            <span className={clsx('text-sm', balanceDue > 0 ? 'text-red-600' : 'text-emerald-600')}>
              Rs. {balanceDue.toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* Signatures */}
      <div className="grid grid-cols-3 gap-8 pt-12 border-t border-gray-200 text-center text-xs text-gray-500">
        <div>
          <div className="border-b border-gray-400 mb-1 w-3/4 mx-auto" />
          <p className="font-medium">Prepared By</p>
        </div>
        <div>
          <div className="border-b border-gray-400 mb-1 w-3/4 mx-auto" />
          <p className="font-medium">Supplier Signature</p>
        </div>
        <div>
          <div className="border-b border-gray-400 mb-1 w-3/4 mx-auto" />
          <p className="font-medium">Authorized Signature</p>
        </div>
      </div>

      {/* System Footer */}
      <div className="mt-8 text-center text-[9px] text-gray-400 border-t border-gray-100 pt-3">
        <p>Gaza Pharmacy Management System • Generated on {new Date().toLocaleString()}</p>
      </div>
    </div>
  );
}

function ThermalLayout({ purchase, settings, pharmacyName, pharmacyAddress, pharmacyPhone, purchaseDate, items, subtotal, discount, tax, grandTotal, paidAmount, balanceDue, paymentStatus }) {
  const FONT_MAP = {
    consolas: "'Consolas', 'Lucida Console', 'Liberation Mono', 'Courier Prime', monospace",
    segoe: "'Segoe UI', -apple-system, BlinkMacSystemFont, Arial, sans-serif",
    'roboto-mono': "'Roboto Mono', 'Space Mono', 'Consolas', monospace",
    lucida: "'Lucida Console', 'Consolas', monospace",
    courier: "'Courier New', Courier, monospace",
  };

  const selectedFontKey = settings?.receipt_font || 'consolas';
  const fontFamily = FONT_MAP[selectedFontKey] || FONT_MAP.consolas;
  const isDarkWeight = settings?.receipt_font_weight !== 'normal';

  return (
    <div
      className={`w-[80mm] max-w-full bg-white p-2 text-black text-[11px] leading-tight select-none border border-gray-300 rounded-lg shadow-sm print:border-none print:shadow-none print:p-0 ${
        isDarkWeight ? 'font-semibold' : 'font-normal'
      }`}
      style={{
        fontFamily,
        WebkitFontSmoothing: 'antialiased',
        textRendering: 'geometricPrecision',
      }}
    >
      <div className="text-center space-y-0.5">
        <PharmacyLogo
          logo={settings?.pharmacy_logo}
          alt={`${pharmacyName} logo`}
          className="mx-auto h-12 w-12 rounded object-contain filter grayscale contrast-150"
          imageClassName="mx-auto h-12 w-12 rounded object-contain filter grayscale contrast-150"
        />
        <h1 className="text-sm font-black uppercase tracking-wider text-black pt-0.5">{pharmacyName}</h1>
        {pharmacyAddress && <p className="text-[10.5px] font-medium text-black leading-tight">{pharmacyAddress}</p>}
        {pharmacyPhone && <p className="text-[10.5px] font-bold text-black leading-tight">Tel: {pharmacyPhone}</p>}
        <p className="text-[10.5px] font-black uppercase mt-1 text-black">*** PURCHASE ORDER ***</p>
      </div>

      <div className="border-t-2 border-dashed border-black my-2" />

      <div className="space-y-0.5 text-[11px] text-black">
        <div className="flex justify-between">
          <span className="font-bold">PO NUMBER:</span>
          <span className="font-bold">{purchase.reference_number}</span>
        </div>
        <div className="flex justify-between">
          <span>DATE:</span>
          <span className="font-semibold">{purchaseDate}</span>
        </div>
        <div className="flex justify-between">
          <span>SUPPLIER:</span>
          <span className="font-bold truncate max-w-[130px]">{purchase.supplier_name || 'General'}</span>
        </div>
        <div className="flex justify-between">
          <span>METHOD:</span>
          <span className="uppercase font-semibold">{purchase.payment_method || 'cash'}</span>
        </div>
        <div className="flex justify-between">
          <span>STATUS:</span>
          <span className="font-bold uppercase">{paymentStatus === 'unpaid' ? 'PENDING' : paymentStatus}</span>
        </div>
      </div>

      <div className="border-t-2 border-dashed border-black my-2" />

      <div>
        <div className="flex justify-between font-bold text-[11px] border-b-2 border-black pb-1 mb-1 text-black">
          <span className="w-[48%]">ITEM</span>
          <span className="w-[14%] text-center">QTY</span>
          <span className="w-[18%] text-right">PRICE</span>
          <span className="w-[20%] text-right">TOTAL</span>
        </div>

        <div className="space-y-1.5">
          {items.map((item, idx) => {
            const uQty = item.unit_quantity || item.quantity;
            const uName = item.unit_name || item.base_unit || '';
            const price = parseFloat(item.buy_price) || 0;
            const itemDisc = parseFloat(item.discount) || 0;
            const total = parseFloat(item.total) || (uQty * price * (1 - itemDisc / 100));

            return (
              <div key={idx} className="flex justify-between items-start text-[11px] leading-tight text-black">
                <div className="w-[48%] pr-1 font-semibold break-words">
                  <p>{item.medicine_name || `Med #${item.medicine_id}`}</p>
                  <p className="text-[10px] text-black font-medium">
                    {uName} {item.batch_number ? `(${item.batch_number})` : ''} {itemDisc > 0 ? `[-${itemDisc}%]` : ''}
                  </p>
                </div>
                <span className="w-[14%] text-center font-bold">{uQty}</span>
                <span className="w-[18%] text-right font-medium">{price.toFixed(2)}</span>
                <span className="w-[20%] text-right font-bold">{total.toFixed(2)}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="border-t-2 border-dashed border-black my-2" />

      <div className="space-y-1 text-[11px] text-black">
        <div className="flex justify-between font-medium">
          <span>SUB-TOTAL:</span>
          <span>Rs. {subtotal.toFixed(2)}</span>
        </div>
        {discount > 0 && (
          <div className="flex justify-between font-semibold">
            <span>DISCOUNT:</span>
            <span>- Rs. {discount.toFixed(2)}</span>
          </div>
        )}
        {tax > 0 && (
          <div className="flex justify-between font-semibold">
            <span>TAX/FREIGHT:</span>
            <span>+ Rs. {tax.toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between text-xs font-black border-t-2 border-b-2 border-black py-1 my-1 text-black">
          <span>NET TOTAL:</span>
          <span>Rs. {grandTotal.toFixed(2)}</span>
        </div>
        <div className="flex justify-between font-medium">
          <span>AMOUNT PAID:</span>
          <span className="font-semibold">Rs. {paidAmount.toFixed(2)}</span>
        </div>
        <div className="flex justify-between font-bold text-black">
          <span>BALANCE DUE:</span>
          <span>Rs. {balanceDue.toFixed(2)}</span>
        </div>
      </div>

      <div className="border-t-2 border-dashed border-black my-2" />

      <div className="text-center space-y-1 text-[10.5px] text-black">
        <p className="font-bold text-black">Purchase Order Record</p>
        <p className="text-[10px] font-bold text-black">POS: Gaza Pharmacy Management System</p>
        <p className="tracking-widest font-black text-[10px] text-black pt-0.5">*** {purchase.reference_number} ***</p>
      </div>
    </div>
  );
}

export default function PurchaseInvoice({ purchase, onClose }) {
  const { settings } = useSettings();
  const [printLayout, setPrintLayout] = useState('standard'); // 'standard' (A4) or 'thermal' (80mm)

  if (!purchase) return null;

  const pharmacyName = settings?.pharmacy_name || 'Gaza Pharmacy';
  const pharmacyAddress = settings?.pharmacy_address || 'Gaza City, Main Street';
  const pharmacyPhone = settings?.pharmacy_phone || '0599123456';
  const pharmacyEmail = settings?.pharmacy_email || 'info@mypharmacy.com';

  const purchaseDate = purchase.purchase_date
    ? new Date(purchase.purchase_date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '-';

  const items = purchase.items || [];
  const subtotal = parseFloat(purchase.subtotal) || 0;
  const discount = parseFloat(purchase.discount) || 0;
  const tax = parseFloat(purchase.tax) || 0;
  const grandTotal = parseFloat(purchase.total) || subtotal + tax - discount;
  const paidAmount = parseFloat(purchase.paid_amount) || 0;
  const balanceDue = parseFloat(purchase.balance_due) || Math.max(0, grandTotal - paidAmount);
  const paymentStatus = purchase.payment_status || (balanceDue > 0 ? (paidAmount > 0 ? 'partial' : 'unpaid') : 'paid');

  const handlePrint = () => {
    window.print();
  };

  const layoutProps = {
    purchase,
    settings,
    pharmacyName,
    pharmacyAddress,
    pharmacyPhone,
    pharmacyEmail,
    purchaseDate,
    items,
    subtotal,
    discount,
    tax,
    grandTotal,
    paidAmount,
    balanceDue,
    paymentStatus,
  };

  return (
    <>
      {/* On-Screen Modal View */}
      <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 overflow-y-auto print:hidden">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-xs"
          onClick={onClose}
        />

        {/* Main Container */}
        <div className="relative w-full max-w-4xl max-h-[92vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col z-10">
          
          {/* Modal Controls Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/90 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                <Printer className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">Purchase Order Invoice</h3>
                <p className="text-xs font-mono text-gray-500">{purchase.reference_number}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex bg-gray-200/80 p-0.5 rounded-lg text-xs font-semibold">
                <button
                  type="button"
                  onClick={() => setPrintLayout('standard')}
                  className={clsx(
                    'px-3 py-1.5 rounded-md transition-colors',
                    printLayout === 'standard' ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  Standard (A4)
                </button>
                <button
                  type="button"
                  onClick={() => setPrintLayout('thermal')}
                  className={clsx(
                    'px-3 py-1.5 rounded-md transition-colors',
                    printLayout === 'thermal' ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  Thermal (80mm)
                </button>
              </div>

              <button
                onClick={handlePrint}
                className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-700 shadow-sm active:scale-95 transition-all"
              >
                <Printer className="h-4 w-4" /> Print / Save PDF
              </button>

              <button
                onClick={onClose}
                className="rounded-lg p-2 text-gray-400 hover:bg-gray-200/70 hover:text-gray-700 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Preview Content Body */}
          <div className="flex-1 p-6 overflow-y-auto bg-gray-100 flex justify-center">
            {printLayout === 'standard' ? (
              <StandardLayout {...layoutProps} />
            ) : (
              <ThermalLayout {...layoutProps} />
            )}
          </div>

        </div>
      </div>

      {/* DEDICATED PRINT CONTAINER MOUNTED AT BODY ROOT (OUTSIDE #root & .fixed) */}
      {createPortal(
        printLayout === 'standard' ? (
          <div id="printable-purchase-export" className="printable-document hidden print:block">
            <StandardLayout {...layoutProps} />
          </div>
        ) : (
          <div id="printable-thermal-receipt" className="hidden print:block">
            <ThermalLayout {...layoutProps} />
          </div>
        ),
        document.body
      )}
    </>
  );
}
